import { Component, lazy, Suspense, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Radar, AlertTriangle } from "lucide-react";

// Modal de rastreo a nivel app (raíz, fuera del Router). Escucha el evento
// `bhgc-open-tracking` despachado por el visor de etiquetas (botón Rastrear)
// y abre el modal de rastreo como un Dialog independiente, NO anidado dentro
// del modal de etiqueta. Así:
//   - un error del chunk de rastreo (maplibre ~800 KB) ya NO lo atrapa el
//     ViewerErrorBoundary del visor (que mostraba "No se pudo cargar el visor
//     de etiquetas", confuso); aquí tiene su propia barrera con mensaje
//     "No se pudo cargar el rastreo".
//   - se evita el Dialog anidado (Dos Radix Dialog apilados) que no abría.
// detail: { shipment } (una guía) o { shipments: [...] } (varias, grupo).

const TrackingModal = lazy(() => import("@/components/tracking/TrackingModal"));
const MultiTrackingModal = lazy(() => import("@/components/tracking/MultiTrackingModal"));

class TrackingErrorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error("[TrackingViewer]", err); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Dialog open onOpenChange={(v) => { if (!v) this.props.onClose?.(); }}>
        <DialogContent className="bg-ink-surface border-ink-border max-w-md p-6 gap-3 text-center">
          <DialogHeader className="space-y-0">
            <DialogTitle className="flex items-center justify-center gap-2 font-display font-bold text-sm uppercase tracking-wide text-ink-text">
              <Radar className="w-4 h-4 text-cyan" /> Rastreo de envío
            </DialogTitle>
          </DialogHeader>
          <AlertTriangle className="w-8 h-8 text-warning mx-auto" />
          <p className="text-sm text-ink-text">No se pudo cargar el rastreo.</p>
          <button type="button" onClick={() => this.props.onClose?.()}
            className="mt-1 inline-flex items-center px-3 h-9 text-sm font-medium border border-ink-border text-ink-text hover:bg-ink-inset mx-auto">
            Cerrar
          </button>
        </DialogContent>
      </Dialog>
    );
  }
}

function LoadingFallback({ onClose }) {
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-ink-surface border-ink-border max-w-md p-6 gap-3 text-center">
        <DialogHeader className="space-y-0">
          <DialogTitle className="flex items-center justify-center gap-2 font-display font-bold text-sm uppercase tracking-wide text-ink-text">
            <Radar className="w-4 h-4 text-cyan" /> Rastreo de envío
          </DialogTitle>
        </DialogHeader>
        <Loader2 className="w-8 h-8 text-cyan animate-spin mx-auto" />
        <p className="text-sm text-ink-muted">Abriendo rastreo…</p>
      </DialogContent>
    </Dialog>
  );
}

export default function TrackingViewerProvider() {
  const [payload, setPayload] = useState(null); // { single, shipment } | { multi, shipments }

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (Array.isArray(d.shipments) && d.shipments.length > 1) {
        setPayload({ multi: true, shipments: d.shipments });
      } else if (d.shipment) {
        setPayload({ single: true, shipment: d.shipment });
      } else if (Array.isArray(d.shipments) && d.shipments.length === 1) {
        setPayload({ single: true, shipment: d.shipments[0] });
      }
    };
    window.addEventListener("bhgc-open-tracking", handler);
    return () => window.removeEventListener("bhgc-open-tracking", handler);
  }, []);

  const close = () => setPayload(null);

  if (!payload) return null;
  return (
    <TrackingErrorBoundary onClose={close}>
      <Suspense fallback={<LoadingFallback onClose={close} />}>
        {payload.single ? (
          <TrackingModal shipment={payload.shipment} onClose={close} />
        ) : (
          <MultiTrackingModal
            items={payload.shipments.map((s) => ({ internal: s }))}
            onClose={close}
          />
        )}
      </Suspense>
    </TrackingErrorBoundary>
  );
}

// Helper para despachar la apertura del modal de rastreo desde cualquier parte
// (usado por el visor de etiquetas). Acepta un Shipment resolved, o un array.
export function openTrackingModal(shipmentOrShipments) {
  const detail = Array.isArray(shipmentOrShipments)
    ? { shipments: shipmentOrShipments }
    : { shipment: shipmentOrShipments };
  window.dispatchEvent(new CustomEvent("bhgc-open-tracking", { detail }));
}